import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SpeechItem from './SpeechItem';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import Header from './Header';
import Footer from './Footer';
import Panel from './Panel';
import HashtagDisplay from './HashtagDisplay';
import PoliticalLeaningBadge from './PoliticalLeaningBadge';
import SpeechSummaryPopup from './SpeechSummaryPopup';
import { NoSpeechesEmptyState } from './EmptyState';
import { generateSpeechSummary } from '../claudeApi';
import PasswordProtection from './PasswordProtection';

const SpeechesPage = () => {
  const { agendaItemId } = useParams();
  const navigate = useNavigate();
  const [speeches, setSpeeches] = useState([]);
  const [agendaItem, setAgendaItem] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryPopupOpen, setSummaryPopupOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState('');

  useEffect(() => {
    if (agendaItemId) {
      fetchSpeechesAndDetails();
    }
  }, [agendaItemId]);

  const fetchSpeechesAndDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch agenda item details
      const { data: agendaData, error: agendaError } = await supabase
        .from('agenda_items')
        .select('*')
        .eq('id', agendaItemId)
        .single();

      if (agendaError) throw agendaError;
      setAgendaItem(agendaData);

      // Fetch meeting details
      const { data: meetingData, error: meetingError } = await supabase
        .from('meetings')
        .select('*')
        .eq('id', agendaData.meeting_id)
        .single();

      if (meetingError) throw meetingError;
      setMeeting(meetingData);

      // Fetch speeches for this agenda item
      const { data: speechesData, error: speechesError } = await supabase
        .from('speeches')
        .select('*')
        .eq('agenda_item_id', agendaItemId)
        .order('time', { ascending: true });

      if (speechesError) throw speechesError;
      setSpeeches(speechesData || []);

    } catch (err) {
      console.error('Error fetching speeches and details:', err);
      setError('Failed to fetch speeches and details');
    } finally {
      setLoading(false);
    }
  };

  // Handle summary button click
  const handleSummaryClick = () => {
    setSummaryPopupOpen(true);
    setSummary('');
  };

  // Generate summary with custom prompt
  const handleGenerateSummary = async (customPrompt) => {
    setSummaryLoading(true);

    try {
      // Generate summary using Claude API with custom prompt
      const summaryText = await generateSpeechSummary(speeches, customPrompt);
      setSummary(summaryText);
    } catch (err) {
      console.error('Error generating summary:', err);
      setSummary(`Error generating summary: ${err.message}`);
    } finally {
      setSummaryLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col transition-colors duration-200">
        <Header />
        <main className="container mx-auto p-4 flex-1 flex items-center justify-center">
          <LoadingSpinner message="Loading speeches..." />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col transition-colors duration-200">
      <Header />

      <main className="container mx-auto p-4 flex-1">
        <ErrorMessage message={error} />

        {/* Breadcrumb Navigation */}
        <nav className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
          <Link 
            to="/meetings" 
            className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            Meetings
          </Link>
          <span>›</span>
          {meeting && (
            <>
              <span>Meeting {meeting.meeting_number}</span>
              <span>›</span>
            </>
          )}
          {agendaItem && (
            <>
              <span>Item {agendaItem.item_number}</span>
              <span>›</span>
            </>
          )}
          <span className="text-gray-900 dark:text-white font-medium">Speeches</span>
        </nav>

        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/meetings')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Meetings
          </button>
        </div>

        {/* Page Header */}
        {agendaItem && meeting && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6 transition-colors duration-200">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Speeches for Agenda Item {agendaItem.item_number}
                </h1>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-lg text-gray-700 dark:text-gray-300">{agendaItem.title}</h2>
                  <PoliticalLeaningBadge leaningScore={agendaItem.leaning_score} />
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                  <span>Meeting {meeting.meeting_number}</span>
                  <span>•</span>
                  <span>{new Date(meeting.date_of_sitting).toLocaleDateString()}</span>
                  <span>•</span>
                  <span>{speeches.length} speech{speeches.length !== 1 ? 'es' : ''}</span>
                </div>
                {agendaItem.hashtags && agendaItem.hashtags.length > 0 && (
                  <HashtagDisplay 
                    hashtags={agendaItem.hashtags}
                    className="mt-2"
                  />
                )}
              </div>
              {speeches.length > 0 && (
                <div className="mt-4 md:mt-0 md:ml-4">
                  <PasswordProtection onAuthenticated={handleSummaryClick}>
                    <button
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Summary
                    </button>
                  </PasswordProtection>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Speeches Content */}
        <div>
          <Panel title={`Speeches (${speeches.length})`}>
            {speeches.length === 0 ? (
              <NoSpeechesEmptyState />
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {speeches.map((speech) => (
                  <SpeechItem key={speech.id} speech={speech} />
                ))}
              </div>
            )}
          </Panel>
        </div>
      </main>

      <Footer />

      {/* Summary Popup */}
      <SpeechSummaryPopup
        isOpen={summaryPopupOpen}
        onClose={() => setSummaryPopupOpen(false)}
        summary={summary}
        loading={summaryLoading}
        onGenerateSummary={handleGenerateSummary}
      />
    </div>
  );
};

export default SpeechesPage;
